import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_QUESTION, needsQuestion } from '../flow/useReadingFlow';
import './QuestionStep.css';

/**
 * The first step of a reading: how it is to be read, and — unless it is a
 * blank reading — the question it answers.
 *
 * Once asked the step is `locked`: the chosen type stays lit and the others
 * dim, the same contract as the spread list above it. The question itself
 * is not shown here afterwards; it is handed to QuestionBanner, which is
 * the one place it lives from then on.
 */
function QuestionStep({ spreadName, modes, mode, question, onChooseMode, onSubmit, locked = false }) {
  const { t } = useTranslation();
  // The question as it is being typed. The flow only hears about it on
  // submit, so a half-typed question never reaches the reading.
  const [draft, setDraft] = useState(question);
  const field = useRef(null);

  const wantsQuestion = needsQuestion(mode);
  const ready = Boolean(mode) && (!wantsQuestion || draft.trim());

  // Coming back to reword (flow's `reask`) starts from what was asked
  useEffect(() => setDraft(question), [question]);

  // Choosing a type that wants a question puts the cursor straight in it
  useEffect(() => {
    if (wantsQuestion && !locked) field.current?.focus();
  }, [mode, wantsQuestion, locked]);

  const submit = (event) => {
    event.preventDefault();
    if (!ready || locked) return;
    onSubmit(draft);
  };

  return (
    <form className="question-step" onSubmit={submit}>
      {/* Eyebrow, title, rule, lead — the shape the spread list above it
          uses, so the two steps read as one series. The spread is named
          here only to say where the reader is; it was chosen from a card
          that described it, and the question is what this step is for. */}
      <header className="question-step__head">
        <p className="question-step__eyebrow">{spreadName}</p>
        <h2 className="question-step__heading">{t('question.heading')}</h2>
        <div className="question-step__rule" aria-hidden="true">
          <span className="question-step__diamond" />
        </div>
        <p className="question-step__lead">{t('question.lead')}</p>
      </header>

      <div className="question-types" role="group" aria-label={t('question.heading')}>
        {modes.map((id) => {
          const chosen = id === mode;
          return (
            <button
              key={id}
              type="button"
              className={`question-type ${chosen ? 'selected' : ''}`}
              onClick={() => onChooseMode(id)}
              aria-pressed={chosen}
              disabled={locked}
            >
              <span className="question-type__name">{t(`question.types.${id}.name`)}</span>
              <span className="question-type__text">{t(`question.types.${id}.text`)}</span>
            </button>
          );
        })}
      </div>

      {/* The field appears with the choice that needs it, and is gone for a
          blank reading — there is nothing to ask. Once the question has been
          asked it goes too: from then on the question is on the page in
          QuestionBanner, and nowhere else. */}
      {wantsQuestion && !locked && (
        <div className="question-field">
          <label className="question-field__label" htmlFor="reading-question">
            {t('question.label')}
          </label>
          <textarea
            id="reading-question"
            ref={field}
            className="form__input question-field__input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            // The question is one line: Enter asks it rather than breaking it
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) submit(event);
            }}
            placeholder={t(`question.placeholder.${mode}`)}
            maxLength={MAX_QUESTION}
            rows={2}
            disabled={locked}
          />
          <p className="question-field__counter" aria-hidden="true">
            {t('question.counter', { n: draft.length, max: MAX_QUESTION })}
          </p>
        </div>
      )}

      {/* Asking is done once and cannot be done again, so the button goes
          with the field rather than staying on as a dead control */}
      {mode && !locked && (
        <div className="question-step__actions">
          <button
            type="submit"
            className="deck-btn deck-btn--primary"
            disabled={!ready}
          >
            <span className="deck-btn__label">
              {wantsQuestion ? t('question.submit') : t('question.submitBlank')}
            </span>
          </button>
        </div>
      )}
    </form>
  );
}

export default QuestionStep;
